import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Form, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import {
  BiFilter,
  BiSearch,
  BiCalendar
} from "react-icons/bi";
import { FcFinePrint } from "react-icons/fc";

import { Link } from "react-router-dom";
import { RegulationContext } from "../../../App";
import Pagination from "../../../components/Pagination/Pagination";
import {
  DEFAULT_ITEMS_PER_PAGE,
  LIST_DEF_SORT,
  SORT_ORDER_DESC
} from "../../../core/constants/common";
import StandardService from "../../../core/service/StandardService";
import Utils from "../../../core/utilities/Utils"; import { FaFileAlt, FaFolder, FaInfo } from "react-icons/fa";
import { SiSap } from "react-icons/si";
import { BsInfo } from "react-icons/bs";

function DataSourceMapping({ t }) {
  const [itemsTotalCount, setItemTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [order, setOrder] = useState(SORT_ORDER_DESC);
  const [sortField, setSortField] = useState(LIST_DEF_SORT);
  const [searchKey, setSearchKey] = useState("");
  const [dataPoints, setDataPoints] = useState([]);
  const [allDataPoints, setAllDataPoints] = useState([]);
  const [isSortingMode, setIsSortingMode] = useState(false);
  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");
  const { glbRegulation } = useContext(RegulationContext);

  const listDataPoints = useCallback((
    stdId,
    pageNumber,
    itemsPerPageVal,
    searchKeyWord,
    sortFieldName,
    fetchAll = false
  ) => {
    if (pageNumber > 0) {
      pageNumber = pageNumber - 1;
    } else {
      setCurrentPage(1);
    }
    let perPage;
    if (itemsPerPageVal > 0) {
      perPage = itemsPerPageVal;
    } else {
      perPage = itemsPerPage;
    }

    // If sorting is applied, fetch all data for proper sorting
    if (fetchAll) {
      StandardService.getAPageOfDps(
        stdId,
        0,
        10000, // Large number to get all data
        true,
        false,
        searchKeyWord,
        "",
      ).then((response) => {
        const totalPages = parseFloat(response.headers["x-total-count"]);
        setItemTotalCount(totalPages);
        setAllDataPoints([...response.data]);
        setIsSortingMode(true);
        setCurrentPage(1);
      }).catch((error) => {
      });
    } else {
      StandardService.getAPageOfDps(
        stdId,
        pageNumber,
        perPage,
        true,
        false,
        searchKeyWord,
        "",
      ).then((response) => {
        const totalPages = parseFloat(response.headers["x-total-count"]);
        setItemTotalCount(totalPages);
        setDataPoints([...response.data]);
      }).catch((error) => {
      });
    }
  }, [itemsPerPage, order]);

  useEffect(() => {
    if (glbRegulation?.id > 0) {
      listDataPoints(glbRegulation?.id, 0, itemsPerPage, searchKey, "", false);
    }
  }, [listDataPoints, searchKey, itemsPerPage, glbRegulation.id]);

  // Debounce search key
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
      // Reset to first page when search changes
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchKey]);

  const handleSort = (val) => {
    if (sortField === val && order === "asc") {
      setOrder("desc");
    } else {
      setOrder("asc");
    }
    setSortField(val);
    
    // Fetch all data when sorting is applied to sort across all items
    if (glbRegulation?.id > 0) {
      listDataPoints(glbRegulation?.id, 0, itemsPerPage, searchKey, val, true);
    }
  };

  const sortDataPoints = (dataPointsToSort, sortFieldName, sortOrder) => {
    if (!dataPointsToSort || dataPointsToSort.length === 0) return dataPointsToSort;

    return [...dataPointsToSort].sort((a, b) => {
      let aValue, bValue;

      switch (sortFieldName) {
        case "name":
          aValue = a.name || "";
          bValue = b.name || "";
          break;
        case "frequency":
          aValue = a.frequency || "";
          bValue = b.frequency || "";
          break;
        case "source":
          aValue = a.riskDataSource?.name || a.riskDataSource?.dataSourceType || "";
          bValue = b.riskDataSource?.name || b.riskDataSource?.dataSourceType || "";
          break;
        default:
          aValue = a[sortFieldName] || "";
          bValue = b[sortFieldName] || "";
      }

      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();

      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  const handleSearch = (e) => {
    const key = e.target.value;
    setSearchKey(key);
    // Reset to normal pagination mode when search changes
    if (isSortingMode) {
      setIsSortingMode(false);
      setAllDataPoints([]);
    }
  };

  const filterDataPoints = (dataPointsToFilter, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return dataPointsToFilter;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    
    return dataPointsToFilter.filter(dataPoint => {
      // Search in data point name
      if (dataPoint.name && dataPoint.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in frequency
      if (dataPoint.frequency && dataPoint.frequency.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in source name
      if (dataPoint.riskDataSource?.name && dataPoint.riskDataSource.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in source type
      if (dataPoint.riskDataSource?.dataSourceType && dataPoint.riskDataSource.dataSourceType.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in metric category
      if (dataPoint.metricCategory && dataPoint.metricCategory.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in groups
      if (dataPoint.groups && dataPoint.groups.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      return false;
    });
  };

  const renderSortIndicator = (field) => {
    if (sortField === field) {
      return order === "asc" ? " ↑" : " ↓";
    }
    return "";
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) {
      return text;
    }
    // Ensure we don't cut in the middle of a word if possible
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    const finalText = lastSpace > maxLength * 0.8 ? truncated.substring(0, lastSpace) : truncated;
    return finalText + ' ...';
  };

  const renderTruncatedText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) {
      return <span>{text}</span>;
    }
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip className="mytooltip">{text}</Tooltip>}
      >
        <span 
          style={{ 
            cursor: 'help', 
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-block',
            maxWidth: '100%'
          }}
          title={text}
        >
          {text}
        </span>
      </OverlayTrigger>
    );
  };


  const getColorDot = (cat) => {
    const colorMap = {
      GOVERNANCE: "esg-bg-gov",
      ENVIRONMENTAL: "esg-bg-env",
      SOCIAL: "esg-bg-soc"
    };
    return (
      <div className={`esg-dot ${colorMap[cat]}`}></div>
    );
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case "SAP":
        return <SiSap color="#0080FF" size={20} />;
      case "Digital Forms":
        return <FaFileAlt color="#8A2BE2" size={20} />;
      case "Files":
        return <FaFolder color="#FFD700" size={20} />;
      default:
        return <FaFileAlt color="#8A2BE2" size={20} />;
    }
  };

  const getFrequencyLabel = (frequency) => {
    if (frequency === 'MONTHLY') {
      return (
        <div className="d-flex align-items-center">
          <BiCalendar color="#3B82F6" size={16} className="me-2" />
          {t("MONTHLY.label")}
        </div>
      );
    } else if (frequency === 'QUARTERLY') {
      return (
        <div className="d-flex align-items-center">
          <BiCalendar color="#10B981" size={16} className="me-2" />
          {t("QUARTERLY.label")}
        </div>
      );
    } else if (frequency === 'HALF_YEARLY') {
      return (
        <div className="d-flex align-items-center">
          <BiCalendar color="#F59E0B" size={16} className="me-2" />
          {t("HALF_YEARLY.label")}
        </div>
      );
    } else if (frequency === 'YEARLY') {
      return (
        <div className="d-flex align-items-center">
          <BiCalendar color="#00CED1" size={16} className="me-2" />
          {t("Yearly.label")}
        </div>
      );
    } else if (frequency === 'ONE_TIME') {
      return (
        <div className="d-flex align-items-center">
          <BiCalendar color="#6B7280" size={16} className="me-2" />
          {t("OneTime.label")}
        </div>
      );
    } else {
      return '';
    }
  };

  return (
    <>
      <div className="FormsList" data-testid="component-name">
        <div className="List_header d-flex justify-content-between align-items-center">
          <div>
            <h5 className="MH">
              {t("DataSourceMapping.label")}
            </h5>
            <p className="text-muted mb-0">
              {t("DataSourceMappingSubTitle.label")}
            </p>
          </div>
          <div className="d-flex align-items-center">
            <div className="List_table-card_Search_01 me-2">
              <div className="List_table-card_Search_Box">
                <Form>
                  <input
                    type="text"
                    className="form-control"
                    value={searchKey}
                    onChange={handleSearch}
                    placeholder={t("SearchDP.label")}
                  />
                  <Link to="#" className="Search_iocnBox">
                    <BiSearch />
                  </Link>
                </Form>
              </div>
            </div>
            {/* <div className="List_table-card_filter">
              <Button variant="outline-secondary" size="sm">
                <BiFilter className="me-1" />
                {t("Filter.label")}
              </Button>
            </div> */}
          </div>
        </div>

        <div className="List_Table_01" style={{ marginTop: "2rem" }}>
          <div className="card List_table-card01">
            <Table responsive="sm" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: "35%", maxWidth: "35%" }}>
                    <Link to="#" onClick={() => handleSort("name")}>
                      {t("dataPoints.label")}{renderSortIndicator("name")}
                    </Link>
                  </th>
                  <th style={{ width: "10%", maxWidth: "10%" }}>
                    <Link to="#" onClick={() => handleSort("frequency")}>
                      {t("Frequency.label")}{renderSortIndicator("frequency")}
                    </Link>
                  </th>
                  <th style={{ width: "25%", maxWidth: "25%" }}>
                    <Link to="#" onClick={() => handleSort("source")}>
                      {t("Source.label")}{renderSortIndicator("source")}
                    </Link>
                  </th>
                  <th>{t("Type.label")}</th>
                  <th width="15%">{t("AssignedGroups.label")}</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const dataToDisplay = isSortingMode ? allDataPoints : dataPoints;
                  const filteredData = filterDataPoints(dataToDisplay, debouncedSearchKey);
                  const sortedData = sortDataPoints(filteredData, sortField, order);
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;
                  const paginatedData = isSortingMode ? sortedData.slice(startIndex, endIndex) : sortedData;
                  
                  return paginatedData?.length > 0
                    ?
                    (
                      paginatedData?.map((dataPoint, index) => (
                      <tr key={dataPoint.id}>
                        <td style={{ width: "35%", maxWidth: "35%" }}>
                          <div className="d-flex align-items-center">
                            {getColorDot(dataPoint.topicCategory)}
                            <span className="px-2" style={{ flex: "1", minWidth: "0", maxWidth: "calc(100% - 20px)" }}>
                              {renderTruncatedText(dataPoint.name, 60)}
                            </span>
                          </div>
                        </td>
                        <td>{getFrequencyLabel(dataPoint.frequency)}</td>
                        <td style={{ width: "25%", maxWidth: "25%" }}>
                          <div className="d-flex align-items-center">
                            <span className="me-2">{getSourceIcon(dataPoint.riskDataSource?.dataSourceType)}</span>
                            <span className="me-3" style={{ flex: "1", minWidth: "0", maxWidth: "calc(100% - 60px)" }}>
                              {renderTruncatedText(
                                dataPoint.riskDataSource?.dataSourceType === 'FIELD' 
                                  ? t('DigitalForms.label') 
                                  : dataPoint.riskDataSource?.name, 
                                40
                              )}
                            </span>
                            {
                              dataPoint.riskDataSource?.dataSourceType === 'FIELD' &&

                              <OverlayTrigger
                                placement="auto"
                                overlay={<Tooltip className="mytooltip">{dataPoint.riskDataSource?.name}</Tooltip>}
                              >
                                <span className="cursor" style={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  backgroundColor: '#f3f4f6',
                                  color: '#8A2BE2'
                                }}>
                                  <BsInfo size={16} />
                                </span>
                              </OverlayTrigger>
                            }
                          </div>
                        </td>
                        <td>{Utils.sentenceCase(dataPoint.metricCategory)}</td>
                        <td>{dataPoint.groups}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        <div className="Nodatafound_box">
                          <h6><FcFinePrint className="me-2" /> {t("NoData.label")}</h6>
                        </div>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </Table>
            <div className="p-2 List_footer_count">
              <Pagination
                itemsTotalCount={isSortingMode ? filterDataPoints(allDataPoints, debouncedSearchKey).length : itemsTotalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                alwaysShown={true}
                setItemsPerPage={setItemsPerPage}
                pageClicked={(pageNum, perPage) => {
                  if (isSortingMode) {
                    // Client-side pagination - just update current page
                    setCurrentPage(pageNum);
                  } else {
                    // Server-side pagination
                    listDataPoints(glbRegulation.id, pageNum, perPage, searchKey, "", false);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

DataSourceMapping.propTypes = {};

export default withTranslation()(DataSourceMapping); 